import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api, setToken } from "@/api";
import { formatError } from "@/components/shared/helpers";
import { Pill } from "@/components/shared/Pill";
import { useAuth } from "@/components/shared/AuthContext";
import { useTranslation } from "@/components/shared/i18n";
import { Globe } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLang } = useTranslation();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setToken(res.data.access_token);
      toast({ title: t("login.success"), description: t("login.success_desc") });
      await refresh();
      const meRes = await api.get("/auth/me");
      const role = meRes.data.role;
      if (meRes.data.must_change_password) {
        navigate("/first-password", { replace: true });
        return;
      }
      navigate(role === "admin" ? "/admin" : "/owner", { replace: true });
    } catch (e) {
      toast({ title: t("login.error"), description: formatError(e) || t("login.error_desc"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060A14] text-slate-50 relative">
      <div
        className="absolute inset-x-0 top-0 h-[380px] bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.28),transparent_60%),radial-gradient(ellipse_at_top_left,rgba(125,211,252,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(59,130,246,0.12),transparent_55%)]"
        aria-hidden
      />

      {/* Language toggle */}
      <div className="absolute top-4 right-4 z-20">
        <button
          data-testid="login-lang-toggle"
          onClick={() => setLang(lang === "fr" ? "en" : "fr")}
          className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.08] px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/10 transition-colors backdrop-blur"
          title={lang === "fr" ? "Switch to English" : "Passer en français"}
        >
          <Globe className="h-3.5 w-3.5" />
          <span className="uppercase">{lang === "fr" ? "EN" : "FR"}</span>
        </button>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div data-testid="login-header" className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.08]">
              <span className="text-xl font-semibold">BM</span>
            </div>
          </div>
          <div data-testid="login-brand" className="text-sm text-slate-300 mb-1">
            Blanc des Montagnes
          </div>
          <h1 data-testid="login-title" className="text-2xl font-bold text-white">
            {t("login.title")}
          </h1>
          <p data-testid="login-subtitle" className="mt-2 text-sm text-slate-300 max-w-sm mx-auto">
            {t("login.subtitle")}
          </p>
          <div data-testid="login-pills" className="mt-4 flex flex-wrap justify-center gap-2">
            <Pill tone="violet">{t("login.pill1")}</Pill>
            <Pill tone="emerald">{t("login.pill2")}</Pill>
            <Pill tone="amber">{t("login.pill3")}</Pill>
          </div>
        </div>

        {from && (
          <div data-testid="login-redirect-info" className="mb-4 px-4 py-2 text-sm bg-white/[0.08] border border-white/10 rounded-xl text-slate-200">
            {t("login.tried_access")} <span className="text-white font-medium">{from}</span>
          </div>
        )}

        <Card data-testid="login-card" className="w-full max-w-sm border-white/10 bg-[#0b0d12]/80 backdrop-blur">
          <CardHeader>
            <CardTitle data-testid="login-card-title" className="text-center text-slate-50">
              {t("login.card_title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1">
                <Label data-testid="login-email-label" htmlFor="login-email">
                  {t("login.email")}
                </Label>
                <Input
                  data-testid="login-email-input"
                  id="login-email"
                  type="email"
                  className="border-white/10 bg-white/[0.08]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label data-testid="login-password-label" htmlFor="login-password">
                  {t("login.password")}
                </Label>
                <Input
                  data-testid="login-password-input"
                  id="login-password"
                  type="password"
                  className="border-white/10 bg-white/[0.08]"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full rounded-full bg-violet-500 text-white hover:bg-violet-400"
                disabled={loading}
              >
                {loading ? t("login.loading") : t("login.submit")}
              </Button>
              <div className="text-center">
                <a
                  data-testid="login-forgot-link"
                  href="/forgot-password"
                  className="text-sm text-violet-400 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/forgot-password");
                  }}
                >
                  {t("login.forgot")}
                </a>
              </div>
            </form>
          </CardContent>
        </Card>

        <div data-testid="login-footer" className="mt-8 text-sm text-slate-400">
          {t("login.need_access")}
        </div>
      </div>
    </div>
  );
}
